import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useCreateReservationMutation,
  useUpdateReservationMutation,
} from '../Service/reservationsApi';
import { useGetReservationWithAssociationsByIdQuery } from '../Service/extendedReservationsApi';
import { initialReservationValues } from '../initialValues';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { ReservationsRoutes } from '../../../Router/path';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import ParticipantsQuickActions from '../Components/ParticipantsQuickActions/ParticipantsQuickActions';
import { useReservationFields } from '../Hooks/useReservationFields';
import { useParticipants } from '../Hooks/useParticipants';
import { useFormHandlers } from '../Hooks/useFormHandlers';
import { submitReservationForm } from '../Helpers/submitReservationForm';
import { filterReservationPartiesWithAddNew } from '../Helpers/filterReservationPartiesWithAddNew';
import { getReservationPartyLookupIds } from '../Helpers/getReservationPartyLookupIds';
import { getOfferIdFromBookings } from '../Helpers/getOfferIdFromBookings';
import { useReservationBookingContext } from '../Hooks/useReservationBookingContext';
import { useGetAllForOfferQuery } from '../../OfferBoardingPlaces/Service/extendedOfferBoardingPlacesApi';
import ReservationPricePreview from '../Components/ReservationPricePreview';
import { useReservationLocalPrices } from '../Hooks/useReservationLocalPrices';
import { ReservationFormState, ReservationPartySelectorField } from '../Types/reservationTypes';
import type { ReservationFormChangeEvent } from '../Types/reservationFormChange';
import { useGetByOfferIdQuery } from '../../OfferOptionsMappings/Service/extendedOfferOptionsMappingsApi';
import { useGetOfferOptionTypesQuery } from '../../OfferOptions/Service/extendedOferOptionsApi';
import { useGetOfferByIdQuery } from '../../Offers/Service/offersApi';
import { useGetBankDocumentByIdQuery } from '../../BankDocuments/Api/bankDocumentsApi';
import { buildOptionTypesForField, toMappingIds } from '../Helpers/offerOptionsUtils';
import { OfferOptionUsageType, OfferOptionType } from '../../OfferOptionsMappings/Types/offerOptionsMappingsTypes';
import {
  updateReservationPartySearchState,
  type ReservationPartySearchReason,
} from '../Helpers/updateReservationPartySearchState';
import {
  useFindOrCreateReservationPartyMutation,
  useSearchableReservationPartiesLookupQuery,
} from '../Service/reservationPartiesLookupApi';
import AddReservationPartyModal from '../Components/AddReservationPartyModal';
import ReservationNotesSection from '../Components/ReservationNotesSection';
import { mapReservationPartyDtoToLookup } from '../Service/reservationPartiesLookupMapper';
import type { ReservationPartyLookup } from '../Service/reservationPartiesLookupMapper';
import type { ReservationPartyLookupDto } from '../Types/reservationTypes';
import { ContractPdfActions } from '../../Contracts';
import ContractGenerateButton from '../../Contracts/Components/ContractGenerateButton';
import { Box, Typography } from '@mui/material';
import { ReservationHeadcountValidationAlert, ReservationRoleSummary } from '../Components';
import {
  buildBankDocumentsQueueReturnUrl,
  parseBankDocumentReservationCreateQuery,
} from '../../Shared/Helpers/bankDocumentReservationCreateNavigation';

const MAX_PARTICIPANTS = 5;
const MIN_SEARCH_LENGTH = 3;
const formatTransferDate = (value?: string | null): string => {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString('pl-PL');
};

const initialReservationPartySearchState: Record<ReservationPartySelectorField, string> = {
  applicantPartyId: '',
  invoiceBuyerPartyId: '',
  fundingPartyId: '',
};

const CreateReservation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bankDocumentsContext = useMemo(
    () => parseBankDocumentReservationCreateQuery(searchParams),
    [searchParams]
  );

  const isBankDocumentsMode = bankDocumentsContext !== null;
  const { data: bankDocument, isLoading: bankDocumentLoading } = useGetBankDocumentByIdQuery(
    bankDocumentsContext?.bankDocumentId ?? 0,
    {
      skip: !isBankDocumentsMode || !bankDocumentsContext?.bankDocumentId,
    }
  );

  const selectedTransferEntry = useMemo(() => {
    if (!bankDocumentsContext || !bankDocument?.entries?.length) {
      return undefined;
    }

    return bankDocument.entries.find((entry) => {
      const legacyEntryId = (entry as { entryId?: number }).entryId;
      return entry.id === bankDocumentsContext.entryId || legacyEntryId === bankDocumentsContext.entryId;
    });
  }, [bankDocument?.entries, bankDocumentsContext]);

  const preselectedBookingId = useMemo(() => {
    const value = searchParams.get('bookingId');
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const redirectAfterSave = useMemo(() => {
    if (isBankDocumentsMode) {
      return undefined;
    }
    const returnTo = searchParams.get('returnTo');
    return returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')
      ? returnTo
      : ReservationsRoutes.root;
  }, [isBankDocumentsMode, searchParams]);

  const shouldShowBackToBookingButton = useMemo(() => {
    const returnTo = searchParams.get('returnTo');
    if (!returnTo) {
      return false;
    }

    return /^\/bookings\/update\/\d+$/.test(returnTo);
  }, [searchParams]);

  const handleBankDocumentsQueueReturn = useCallback(
    (response: { reservationId?: unknown }, shouldRedirect: boolean) => {
      if (!bankDocumentsContext || !shouldRedirect) {
        return false;
      }

      const reservationId =
        typeof response?.reservationId === 'number' &&
        Number.isInteger(response.reservationId) &&
        response.reservationId > 0
          ? response.reservationId
          : null;

      if (!reservationId) {
        return false;
      }

      const returnUrl = buildBankDocumentsQueueReturnUrl({
        entryId: bankDocumentsContext.entryId,
        bankDocumentId: bankDocumentsContext.bankDocumentId,
        reservationId,
        companyId: bankDocumentsContext.companyId,
      });
      navigate(returnUrl);

      return true;
    },
    [bankDocumentsContext, navigate]
  );

  // === MUTATIONS (Create/Update Reservation) ===
  // Hooks for API mutations to create or update reservation
  const [createReservation, createStatus] = useCreateReservationMutation();
  const [updateReservation, updateStatus] = useUpdateReservationMutation();
  const [findOrCreateReservationParty] = useFindOrCreateReservationPartyMutation();

  // === LOCAL STATE ===
  // Local React state for reservation, applicant, and form data
  const [createdReservationId, setCreatedReservationId] = useState<number | null>(null);
  const [activeReservationPartySearchField, setActiveReservationPartySearchField] =
    useState<ReservationPartySelectorField>('applicantPartyId');
  const [anyPartyDropdownOpened, setAnyPartyDropdownOpened] = useState(false);
  const [reservationPartySearches, setReservationPartySearches] = useState(
    initialReservationPartySearchState
  );
  const activeReservationPartySearch =
    reservationPartySearches[activeReservationPartySearchField];
  const [formData, setFormData] = useState<ReservationFormState>({
    ...initialReservationValues,
    bookingId: preselectedBookingId ?? initialReservationValues.bookingId,
  });
  const today = new Date().toISOString().slice(0, 10);
  const reservationPartyLookupIds = getReservationPartyLookupIds(formData);

  const [autocompleteResetKeys, setAutocompleteResetKeys] = useState<Record<string, number>>({});

  // === PARTY MODAL STATE ===
  const [addPartyModalOpen, setAddPartyModalOpen] = useState(false);
  const [activePartyField, setActivePartyField] = useState<ReservationPartySelectorField>('applicantPartyId');
  const [additionalParties, setAdditionalParties] = useState<ReservationPartyLookup[]>([]);

  const createReservationPartySearchHandler = useCallback(
    (fieldName: ReservationPartySelectorField) =>
    (inputValue: string, reason?: ReservationPartySearchReason) => {
      if (reason === 'open') {
        setActiveReservationPartySearchField(fieldName);
        setAnyPartyDropdownOpened(true);
        return; // do NOT reset search to '' on open
      }
      setReservationPartySearches((currentSearches) => {
        const nextState = updateReservationPartySearchState({
          currentSearches,
          fieldName,
          inputValue,
          reason,
        });

        setActiveReservationPartySearchField(nextState.activeFieldName);

        return nextState.searches;
      });
    },
    []
  );

  // === LOOKUPS (API Data for Selects/Autocomplete) ===
  // Queries for reservation parties and selected booking context
  const {
    selectedBookingContext,
    bookingLookupOptions,
    selectedBookingLoading,
    bookingLookupLoading,
    handleBookingLookupInputChange,
  } = useReservationBookingContext({
    bookingId: Number(formData.bookingId) || null,
  });
  const partySearchEnabled =
    anyPartyDropdownOpened ||
    activeReservationPartySearch.trim().length >= MIN_SEARCH_LENGTH ||
    reservationPartyLookupIds.length > 0;
  const partyQueryArgs = useMemo(
    () => ({
      search: activeReservationPartySearch.trim() || undefined,
      ids: reservationPartyLookupIds.length > 0 ? reservationPartyLookupIds : undefined,
      take: activeReservationPartySearch.trim() ? 200 : 20,
    }),
    [activeReservationPartySearch, reservationPartyLookupIds]
  );
  const { parties: lookupParties, isLoading: reservationPartiesLoading } =
    useSearchableReservationPartiesLookupQuery(partyQueryArgs, !partySearchEnabled);
  const allParties = useMemo(() => {
    const seen = new Set<number>();
    return [...lookupParties, ...additionalParties].filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [lookupParties, additionalParties]);
  const offerId = getOfferIdFromBookings(selectedBookingContext);
  const shouldFetchOfferNameFallback = Boolean(
    selectedBookingContext && offerId && !selectedBookingContext.offerName?.trim()
  );
  const { data: selectedOffer } = useGetOfferByIdQuery(offerId ?? 0, {
    skip: !shouldFetchOfferNameFallback,
  });
  const { data: offerBoardingPlaces, isLoading: offerBoardingPlacesLoading } =
    useGetAllForOfferQuery(offerId ?? 0, {
      skip: !offerId,
    });
  const { data: offerOptionsMappings, isLoading: offerOptionsMappingsLoading } = useGetByOfferIdQuery(
    offerId ?? 0,
    { skip: !offerId }
  );
  const { data: offerOptionTypes, isLoading: offerOptionTypesLoading } = useGetOfferOptionTypesQuery();
  const previousOfferIdRef = useRef<number | null | undefined>(undefined);
  const mappedOfferOptionTypes: OfferOptionType[] = useMemo(
    () =>
      (offerOptionTypes ?? []).map(({ id, name, offerOptionsValues }) => ({
        id,
        name,
        offerOptionsValues,
      })),
    [offerOptionTypes]
  );
  const additionalOptionTypes = useMemo(
    () => buildOptionTypesForField(mappedOfferOptionTypes, offerOptionsMappings ?? []),
    [mappedOfferOptionTypes, offerOptionsMappings]
  );
  const hasOfferOptions = useMemo(
    () =>
      (offerOptionsMappings ?? []).some(
        (mapping) => mapping.usageType === OfferOptionUsageType.Additional
      ),
    [offerOptionsMappings]
  );
  const selectedAdditionalOptionMappingIds = useMemo(
    () => toMappingIds(formData.selectedOfferOptions ?? [], offerOptionsMappings ?? []),
    [formData.selectedOfferOptions, offerOptionsMappings]
  );

  useEffect(() => {
    if (previousOfferIdRef.current !== undefined && previousOfferIdRef.current !== offerId) {
      setFormData((prev) => ({
        ...prev,
        selectedOfferOptions: [],
      }));
    }
    previousOfferIdRef.current = offerId;
  }, [offerId]);

  const handleAddNote = useCallback((content: string) => {
    setFormData((prev) => ({
      ...prev,
      notes: [...prev.notes, { content }],
    }));
  }, []);

  // Local note drafts never carry a backend-assigned id (create/update responses don't
  // return one), so once the reservation exists, refetch it to recover real note ids.
  // Without this, saving again while still on this page (e.g. "Zapisz i kontynuuj edycję")
  // would resend every earlier note without an id and the backend would duplicate them.
  const { data: createdReservationData } = useGetReservationWithAssociationsByIdQuery(
    createdReservationId ?? 0,
    { skip: !createdReservationId }
  );

  useEffect(() => {
    if (createdReservationData) {
      setFormData((prev) => ({
        ...prev,
        notes: createdReservationData.notes,
      }));
    }
  }, [createdReservationData]);

  // === DYNAMIC FORM FIELDS ===
  // Generate reservation and applicant form fields based on lookups and form state
  const dynamicFields = useReservationFields({
    formData,
    bookingLookupOptions,
    bookingLookupLoading: bookingLookupLoading || selectedBookingLoading,
    hideBookingField: Boolean(preselectedBookingId),
    reservationParties: allParties,
    reservationPartiesLoading,
    offerBoardingPlaces: offerBoardingPlaces ?? [],
    offerBoardingPlacesLoading,
    offerOptionTypes: additionalOptionTypes,
    offerOptionTypesLoading: offerOptionsMappingsLoading || offerOptionTypesLoading,
    hasOfferOptions,
  });

  const preselectedBookingSummary = useMemo(() => {
    if (!preselectedBookingId) {
      return null;
    }

    if (selectedBookingLoading) {
      return 'Ładowanie danych wybranej rezerwacji...';
    }

    if (!selectedBookingContext) {
      return `Nie udało się załadować rezerwacji #${preselectedBookingId}.`;
    }

    const bookingTitle = selectedBookingContext.offerName?.trim() || selectedOffer?.name?.trim();
    const bookingDates = `${selectedBookingContext.bookingDateFrom} >>> ${selectedBookingContext.bookingDateTo}`;

    return bookingTitle ? `Termin ${bookingTitle} ${bookingDates}` : `Termin ${bookingDates}`;
  }, [preselectedBookingId, selectedBookingContext, selectedBookingLoading, selectedOffer?.name]);

  const transferSummaryRows = useMemo(() => {
    if (!selectedTransferEntry) {
      return [];
    }

    const currency = selectedTransferEntry.currency?.trim() || bankDocument?.currency || 'PLN';
    return [
      { label: 'Kwota', value: `${selectedTransferEntry.amount.toFixed(2)} ${currency}` },
      { label: 'Data operacji', value: formatTransferDate(selectedTransferEntry.bookingDate) },
      { label: 'Kontrahent', value: selectedTransferEntry.counterpartyName ?? '-' },
      { label: 'Tytuł', value: selectedTransferEntry.title ?? '-' },
      { label: 'Rachunek', value: selectedTransferEntry.counterpartyAccountNumber ?? '-' },
      { label: 'Referencja', value: selectedTransferEntry.reference ?? '-' },
    ];
  }, [bankDocument?.currency, selectedTransferEntry]);

  // === FORM LOGIC HOOKS ===
  // Handlers for form field changes (onChange) and participants management
  const { handleChange } = useFormHandlers(setFormData);

  const handleChangeWithModal = useCallback(
    (e: ReservationFormChangeEvent, child?: React.ReactNode) => {
      const { name, value } = e.target;
      if (!name) {
        handleChange(e, child);
        return;
      }
      const PARTY_FIELDS: ReservationPartySelectorField[] = [
        'applicantPartyId',
        'invoiceBuyerPartyId',
        'fundingPartyId',
      ];
      if (PARTY_FIELDS.includes(name as ReservationPartySelectorField) && value === '__new__') {
        setAutocompleteResetKeys((k) => ({ ...k, [name]: (k[name] ?? 0) + 1 }));
        setActivePartyField(name as ReservationPartySelectorField);
        setAddPartyModalOpen(true);
        return;
      }
      handleChange(e, child);
    },
    [handleChange]
  );

  const handlePartyCreated = useCallback(
    (party: ReservationPartyLookupDto, field: ReservationPartySelectorField) => {
      const lookup = mapReservationPartyDtoToLookup(party);
      if (lookup) {
        setAdditionalParties((prev) => [...prev, lookup]);
      }
      handleChange(
        {
          target: { name: field, value: party.id },
        },
        undefined
      );
    },
    [handleChange]
  );

  const {
    handleAddEmptyParticipant,
    handleAddParticipantFromApplicant,
    maxReached,
    disabledFromApplicant,
  } = useParticipants({
    formData,
    setFormData,
    applicantById: undefined,
    today,
    maxParticipants: MAX_PARTICIPANTS,
    applicantLoading: false,
  });

  // === FORM CONFIGURATION ===
  // Main form logic hook - provides submitFn, groupOrder, mutation status etc.
  const entityForm = useEntityForm<ReservationFormState>({
    initialData: formData,
    fields: dynamicFields,
    submitFn: async (data) =>
        submitReservationForm({
          data,
          formData,
          createdReservationId,
          setCreatedReservationId,
          createReservation: (payload) => createReservation(payload).unwrap(),
          updateReservation: (payload) => updateReservation(payload).unwrap(),
          findOrCreateReservationParty,
          offerOptionsMappings: offerOptionsMappings ?? [],
          offerOptionsMappingsLoading,
        }),
    redirectUrl: redirectAfterSave,
    backUrl: shouldShowBackToBookingButton ? redirectAfterSave : undefined,
    onSuccessWithResponse: isBankDocumentsMode ? handleBankDocumentsQueueReturn : undefined,
    mutationStatus: createdReservationId ? updateStatus : createStatus,
    groupOrder: [
      FormRenderGroupTypes.basic,
      FormRenderGroupTypes.participants,
    ],
  });
  // === PRICE PREVIEW ===
  // Reservation price preview component to show calculated prices based on form data
  const boardingPlacesPreview = offerBoardingPlaces
    ? offerBoardingPlaces.map((bp) => ({
        id: bp.id,
        name: bp.city, // Ensure OfferBoardingPlace has a 'name' property or map accordingly
        price: bp.price,
      }))
    : undefined;

  const { localUnitPrice, localBoardingPlacePrice } = useReservationLocalPrices({
    selectedBookingContext,
    offerBoardingPlaces: boardingPlacesPreview,
    offerBoardingPlaceId: formData.offerBoardingPlaceId,
  });

  // === RENDER (UI) ===
  // Renders the EntityFormLayout component with all handlers and extra actions
  return (
    <>
      {isBankDocumentsMode && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: 'background.paper',
          }}
          data-testid="create-reservation-bank-document-entry-summary"
        >
          <Typography variant="subtitle2" gutterBottom>
            Dane przelewu
          </Typography>
          {bankDocumentLoading && (
            <Typography variant="body2" color="text.secondary">
              Ładowanie danych przelewu...
            </Typography>
          )}
          {!bankDocumentLoading && !selectedTransferEntry && (
            <Typography variant="body2" color="text.secondary">
              Nie udało się załadować danych przelewu.
            </Typography>
          )}
          {!bankDocumentLoading &&
            selectedTransferEntry &&
            transferSummaryRows.map((row) => (
              <Typography key={row.label} variant="body2">
                <strong>{row.label}:</strong> {row.value}
              </Typography>
            ))}
        </Box>
      )}
      {preselectedBookingId && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: 'background.paper',
          }}
          data-testid="create-reservation-preselected-booking-summary"
        >
          <Typography variant="subtitle2" gutterBottom>
            Wybrana rezerwacja
          </Typography>
          <Typography variant="body2">{preselectedBookingSummary}</Typography>
        </Box>
      )}
      <ReservationRoleSummary
        applicantPartyId={Number(formData.applicantPartyId) || undefined}
        invoiceBuyerPartyId={Number(formData.invoiceBuyerPartyId) || undefined}
        fundingPartyId={Number(formData.fundingPartyId) || undefined}
        parties={allParties}
      />
      <ReservationHeadcountValidationAlert
        participantsCount={Number(formData.participants?.length) || 0}
        declaredParticipantsCount={Number(formData.declaredParticipantsCount) || 0}
      />
      <div
        style={{
          position: 'fixed',
          top: 100,
          right: 32,
          zIndex: 1200,
          width: 340,
          maxWidth: '90vw',
        }}
      >
        <ReservationPricePreview
          bookingId={Number(formData.bookingId)}
          selectedBoardingPlaceId={Number(formData.offerBoardingPlaceId)}
          numberOfParticipants={
            Number(formData.participants?.length) > 0
              ? Number(formData.participants?.length)
              : Number(formData.declaredParticipantsCount) || 0
          }
          localUnitPrice={localUnitPrice}
          localBoardingPlacePrice={localBoardingPlacePrice}
          selectedAdditionalOptionMappingIds={selectedAdditionalOptionMappingIds}
          discountAmount={Number(formData.discountAmount) || null}
          totalPriceOverride={Number(formData.totalPriceOverride) || null}
        />
      </div>
      <AddReservationPartyModal
        open={addPartyModalOpen}
        field={activePartyField}
        onClose={() => setAddPartyModalOpen(false)}
        onCreated={handlePartyCreated}
      />
      <EntityFormLayout
        {...entityForm}
        formHandlers={{
          ...entityForm.formHandlers,
          onChange: handleChangeWithModal,
        }}
        customFilterOptions={{
          ...(preselectedBookingId
            ? {}
            : {
                bookingId: (options) => options,
              }),
          applicantPartyId: filterReservationPartiesWithAddNew,
          invoiceBuyerPartyId: filterReservationPartiesWithAddNew,
          fundingPartyId: filterReservationPartiesWithAddNew,
        }}
        autocompleteInputChangeHandlers={{
          ...(preselectedBookingId
            ? {}
            : {
                bookingId: handleBookingLookupInputChange,
              }),
          applicantPartyId: createReservationPartySearchHandler('applicantPartyId'),
          invoiceBuyerPartyId: createReservationPartySearchHandler('invoiceBuyerPartyId'),
          fundingPartyId: createReservationPartySearchHandler('fundingPartyId'),
        }}
        autocompleteResetKeys={autocompleteResetKeys}
        participantsActions={
          <ParticipantsQuickActions
            onAddEmpty={handleAddEmptyParticipant}
            onAddFromApplicant={handleAddParticipantFromApplicant}
            disabledFromApplicant={disabledFromApplicant}
            maxReached={maxReached}
            showAddFromApplicant={false}
          />
        }
      >
        {createdReservationId !== null && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <ContractGenerateButton reservationId={createdReservationId} templateId={1} />
            <ContractPdfActions
              reservationId={createdReservationId}
              contractName={`Umowa_Rezerwacja_${createdReservationId}.pdf`}
            />
          </Box>
        )}
        <ReservationNotesSection notes={formData.notes} onAddNote={handleAddNote} />
      </EntityFormLayout>
    </>
  );
};

export default CreateReservation;
